/**
 * Decide whether the LinkAccountModal should auto-open on the Account
 * page. Extracted from `Account/index.tsx` so the logic is unit-testable
 * without mounting the full page.
 *
 * The decision folds two linkage signals:
 * - `hasAnyLinkedAccount` — auth-store view. Sourced from
 *   /google-ads/accounts-summary + /meta/accounts-summary. The Meta
 *   endpoint filters `is_active=True`, so a member with an inactive
 *   Meta credential row reads as unlinked here.
 * - `backendHasAnyLinkedPlatform` — /billing/linkage-eligibility view.
 *   Derived from the endpoint's `has_linked_google` / `has_linked_meta`
 *   fields, which the backend populates by calling `has_linked_platform`
 *   directly (ignores `is_active`). This is a DB-level linkage signal,
 *   distinct from `can_link_*` which expresses permission and is always
 *   True for Pro members regardless of what's linked — inverting
 *   `can_link_*` would silently break the moment Pro ships.
 *
 * When the two disagree (auth store empty, backend says linked), we
 * trust the backend and suppress the popup — otherwise the modal would
 * pitch a Meta re-link that /auth/meta/authorize is going to refuse
 * with a 409.
 *
 * Fail-closed on eligibility error: if the query hard-fails we can't
 * verify the backend view, so we do NOT fire the popup. The empty-state
 * card on the page still renders a "Link Ads Account" button, so the
 * user can act — they just don't get the auto-open.
 */
export interface LinkAccountModalGateInputs {
  isLoading: boolean;
  hasGoogleCampaigns: boolean;
  hasAnyLinkedAccount: boolean;
  backendHasAnyLinkedPlatform: boolean;
  isEligibilityError: boolean;
}

export function shouldShowLinkAccountModal({
  isLoading,
  hasGoogleCampaigns,
  hasAnyLinkedAccount,
  backendHasAnyLinkedPlatform,
  isEligibilityError,
}: LinkAccountModalGateInputs): boolean {
  if (isLoading) return false;
  if (hasGoogleCampaigns) return false;
  if (hasAnyLinkedAccount) return false;
  if (backendHasAnyLinkedPlatform) return false;
  if (isEligibilityError) return false;
  return true;
}
